import { FC, ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router';

import { useUpdateSignatureMutation, useUploadSignatureImageMutation } from '../../api/queries/signature';
import Alert from '../../components/Alert/Alert';
import Button from '../../components/Button';
import CheckBoxInput from '../../components/CheckBoxInput/CheckBoxInput';
import { Signature, SignatureImageUploadBody } from '../../interfaces/Signature';
import { sendSignatureDetailsUpdated } from '../../services/Analytics';
import { generateSignedPostPayload } from '../../services/Aws';
import usePageTitle from '../../services/hooks/usePageTitle';
import { SignatureOutletContext } from '../../services/LoaderService';
import MoneyService from '../../services/MoneyService';
import CompletedSignature from './components/CompletedSignature/CompletedSignature';
import ProductsSection from './components/ProductsSection/ProductsSection';
import SignatureCanvas from './components/SignatureCanvas/SignatureCanvas';
import styles from './Signature.module.scss';


const SignatureComponent: FC = (): ReactElement | null => {
  const { t } = useTranslation();
  const { signature: defaultSignature } = useLoaderData() as SignatureOutletContext;
  const { updateSignature, isUpdating } = useUpdateSignatureMutation();
  const { uploadSignatureImage, isUploading } = useUploadSignatureImageMutation();

  const [signature, setSignature] = useState<Signature>(defaultSignature);
  const [customerAgreedToSign, setCustomerAgreedToSign] = useState<boolean>(true);
  const [isInErrorMode, setIsInErrorMode] = useState<boolean>(false);
  const [image, setImage] = useState<SignatureImageUploadBody['file'] | null>(null);

  usePageTitle(t('pageTitle:signature'));

  const onSave = useCallback(async (signature: Signature) => {
    const signatureUpdateParams = { completed: true, customerSigned: customerAgreedToSign };

    try {
      if (customerAgreedToSign && image) {
        const payload = generateSignedPostPayload({
          ...signature.presignedUploadFields,
          file: image,
        });
        await uploadSignatureImage({
          presignedUploadUrl: signature.presignedUploadUrl,
          body: payload
        });
      }
      await updateSignature({ hashId: signature.hashId, ...signatureUpdateParams });

      if (!isInErrorMode) {
        sendSignatureDetailsUpdated({ customerSigned: customerAgreedToSign, imageSize: image?.size });
      }
      setSignature({ ...signature, ...signatureUpdateParams });
    } catch {
      setIsInErrorMode(true);
    }
  }, [image, customerAgreedToSign, setSignature, isInErrorMode, updateSignature, uploadSignatureImage]);

  const onImageChange = useCallback((image: SignatureImageUploadBody['file'] | null) => {
    setImage(image);
    setIsInErrorMode(false);
  }, []);

  const {
    customerSigned, completed, productsConsumed,
    expertComment, productsReturned, totalPrice,
    customerApprovalText
  } = signature;

  if (completed) {
    return (
      <CompletedSignature customerSigned={customerSigned} />
    );
  }

  return (
    <div className={styles.signatureContainer}>
      <h1>{t('signature:title')}</h1>
      <ProductsSection data={productsConsumed} title={t('signature:productsConsumed')} />
      {!!productsReturned.length && (
        <ProductsSection data={productsReturned} title={t('signature:productsReturned')} />
      )}
      <div className={styles.totalPriceContainer}>
        <label>{t('signature:total')} <span className={styles.vatLabel}>{t('signature:includingVat')}</span></label>
        <span>{MoneyService.format(totalPrice)}</span>
      </div>
      <div className={styles.separator}></div>

      <div className={styles.customerInformationSection}>
        <h2>{t('signature:customerInformation')}</h2>
        <p className={styles.expertNoteTitle}>{t('signature:expertComment')}</p>
        <p className={styles.expertNote}>{expertComment}</p>
      </div>

      {!!customerApprovalText && (
        <div className={styles.customerTermsSection}>
          <h2>{t('signature:customerTerms')}</h2>
          <p>{customerApprovalText}</p>
        </div>
      )}

      <h2>{t('signature:signVisit')}</h2>

      {!!isInErrorMode && (
        <Alert level="error" text="Something went wrong" />
      )} {/* TODO: Error handling message when PUT and PATCH methods fail */}

      <SignatureCanvas
        disabled={!customerAgreedToSign}
        onClear={() => onImageChange(null)}
        onDraw={(image) => onImageChange(image)}
      />

      <CheckBoxInput
        id="correct"
        isChecked={!customerAgreedToSign}
        label={t('signature:declineSign')}
        onCheck={(isChecked) => {
          setIsInErrorMode(false);
          setCustomerAgreedToSign(!isChecked);
          if (customerAgreedToSign && image) {
            setImage(null);
          }
        }}
      />

      <div className={styles.buttonContainer}>
        <Button
          disabled={(!image && customerAgreedToSign) || isUploading || isUpdating}
          onClick={() => onSave(signature)}
          text={t('shared:agree')}
        />
      </div>
    </div>
  );
};

export default SignatureComponent;
